import React from "react";
import { Link } from "react-router-dom";
import WhyTraditionalSoap from "./WhyTraditionalSoap";
import { useScrollToHash } from "../lib/useScrollToHash";

const IMG_SALT = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/u7skbuj9_Alati%20signature%20salt%20bar%20tallow%20soap%20infused%20with%20Indigo%20powder.jpg";
const IMG_RHUBARB = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/judl7qlx_Infused%20with%20Himalayan%20Rhubarb%20Unscented%20Tallow%20soap.jpg";
const IMG_CACAO = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/2mrz3li6_Infused%20with%20Cacao%20Powder%20organic%20Tallow%20soap.jpg";
const IMG_MADDER = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/moho1bn4_Infused%20with%20Madder%20Root%20Cinnamon%20Activated%20charcoal%20tallow%20soap.jpg";

const Section = ({ children, className = "" }) => (
    <section className={`px-6 lg:px-10 ${className}`}>
        <div className="max-w-2xl mx-auto">{children}</div>
    </section>
);

const OurStory = () => {
    useScrollToHash();
    return (
        <div data-testid="our-story-page" className="bg-[#FAF9F6]">
            <section className="py-20 lg:py-28 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto">
                    <p className="label">Our Story</p>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-5 leading-[1.04]">
                        Soap has been part of our family story for generations.
                    </h1>
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img
                        src={IMG_SALT}
                        alt="ALATI Signature Salt Bar"
                        className="w-full h-full object-cover"
                    />
                </div>
            </section>

            <Section className="pb-16" data-testid="story-opening">
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]" data-testid="story-body">
                    <p>
                        Long before ALATI existed, the women in our family on the Greek island
                        of Kalymnos made traditional soap by hand.
                    </p>
                    <p>
                        They used what the land and sea gave them. Fresh olive oil. Mountain
                        herbs. Rosemary. Thyme. Sea salt. Animal fats when needed. Time.
                        Patience. Knowledge passed from one generation to the next.
                    </p>
                    <p>
                        This was not luxury. This was everyday life. They washed their bodies
                        with it. They washed their hair with it. They washed their clothes with
                        it. Soap was made properly because that was simply how things were
                        done.
                    </p>
                </div>
            </Section>

            <Section className="pb-16">
                <h2 className="font-serif text-3xl sm:text-4xl mb-6 leading-snug text-[#1C1C1A]">
                    Kalymnos. Family heritage.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        The family tradition comes from Kalymnos in the Dodecanese, Greece.
                        From the stories passed down through our family, this tradition
                        reaches back at least to the late 1800s, and likely much earlier.
                    </p>
                    <p>
                        My grandmother Poppy made traditional olive oil soap. She infused
                        olive oil with thyme, rosemary, sage, Greek mountain herbs and other
                        botanicals from the island. The way she worked &mdash; quietly, slowly,
                        using what the land gave &mdash; still inspires the way ALATI works
                        today.
                    </p>
                </div>
            </Section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="product-image-wrap aspect-[4/5]">
                        <img src={IMG_RHUBARB} alt="Himalayan rhubarb tallow soap" className="w-full h-full object-cover" />
                    </div>
                    <div className="product-image-wrap aspect-[4/5]">
                        <img src={IMG_CACAO} alt="Organic Cacao Tallow Soap" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            <Section className="pb-16">
                <p className="label">Darwin &mdash; The Next Chapter</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    The story began in Greece. The next chapter is written in Darwin.
                </h2>
                <p className="text-lg leading-relaxed text-[#4A4A46]">
                    ALATI is handcrafted in Darwin, Australia. This is not pretending to be
                    European. This is Greek family tradition continued in Australia&rsquo;s Top
                    End.
                </p>
            </Section>

            <Section className="pb-16">
                <h2 className="font-serif text-3xl sm:text-4xl mb-6 leading-snug text-[#1C1C1A]">
                    Chrissa&rsquo;s journey.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        After cancer treatment, chemotherapy and stem cell transplant
                        recovery, I became deeply aware of what touched my skin every day. My
                        skin had become dry, fragile, irritated and uncomfortable.
                    </p>
                    <p>
                        Alongside medical care, I began returning to simple ingredients and
                        traditional soap making. I began making soap in 2020. I learned from
                        family history, from my mother, from courses, from study and from
                        practice. I have since trained in both hot process and cold process
                        soap making.
                    </p>
                    <p className="italic text-[#1C1C1A]">
                        This is my own experience, not a medical claim. But returning to
                        simple, properly made soap changed the way I thought about what we
                        use every day.
                    </p>
                </div>
            </Section>

            <Section className="pb-16">
                <p className="label">Castile Soap</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    Some things should never be rushed.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        I also make true Castile soap. Castile is pure olive oil soap. It
                        takes patience. It can take many months &mdash; sometimes close to a
                        year &mdash; to fully cure and mature.
                    </p>
                    <p>
                        It is slow, simple, and deeply connected to the family story.
                    </p>
                </div>
            </Section>

            <Section className="pb-16">
                <p className="label">Tallow Soap</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    Good soap does not need to be complicated. It needs to be made properly.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        ALATI also makes tallow soap &mdash; another old-world tradition. In
                        older communities, people made soap using what they had. In
                        olive-growing regions, olive oil was used. In farming communities,
                        animal fats such as goat, sheep or beef fat were used.
                    </p>
                    <p>
                        We use grass-fed tallow because it creates a beautiful, creamy,
                        long-lasting bar.
                    </p>
                    <p>
                        Over the years I experimented with many traditional soap recipes. I
                        first learned traditional olive oil Castile soap inspired by my
                        grandmother and family traditions from Kalymnos. As my own soap
                        making developed, I discovered that handcrafted tallow soap became
                        the foundation of ALATI &mdash; because of its beautiful lather,
                        longevity, traditional history, and simplicity.
                    </p>
                </div>
            </Section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img src={IMG_MADDER} alt="Madder root and charcoal tallow soap" className="w-full h-full object-cover" />
                </div>
            </section>

            <Section className="pb-16">
                <p className="label">Adonis</p>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 mb-6 leading-snug text-[#1C1C1A]">
                    A son, learning beside his mum.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                    <p>
                        After my second stem cell transplant, my son Adonis &mdash; then
                        thirteen &mdash; began learning beside me. He wanted to help his mum.
                        He also wanted to learn how to build something meaningful.
                    </p>
                    <p>
                        His simple statement became part of ALATI:
                    </p>
                </div>
                <blockquote
                    className="my-10 border-l border-[#1C1C1A] pl-8 font-serif text-3xl sm:text-4xl leading-snug text-[#1C1C1A]"
                    data-testid="adonis-quote"
                >
                    &ldquo;It&rsquo;s not just what goes in your body, it&rsquo;s what goes on
                    your body too.&rdquo;
                    <span className="block mt-4 text-sm uppercase tracking-[0.22em] text-[#737562]">
                        &mdash; Adonis
                    </span>
                </blockquote>
            </Section>

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">Made the old way. Trusted today.</p>
                    <h2 className="font-serif text-4xl mt-5">Find your everyday bar.</h2>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <Link to="/shop" className="btn-primary" data-testid="story-shop-cta">
                            Shop Soap
                        </Link>
                        <a href="#tradition" className="btn-secondary" data-testid="story-tradition-cta">
                            Why Traditional Soap
                        </a>
                    </div>
                </div>
            </section>

            {/* TRADITION section — merged from former /tradition page */}
            <div id="tradition" data-testid="tradition-anchor">
                <WhyTraditionalSoap />
            </div>
        </div>
    );
};

export default OurStory;
